import * as arrow from "apache-arrow";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  openParquetFileFromUrl,
  readChunkFromParquetFile,
  type ParquetChunkResult,
} from "./parquet";
import { getRowFromArrowTable } from "./mui_table_utils";

/** Small for easy testing; increase (e.g. 10_000) for production. */
export const CHUNKED_PARQUET_CHUNK_SIZE = 50;

export type ChunkedParquetProviderParams = {
  downloadUrl: string;
  totalRowCount: number;
  chunkSize?: number;
  getRefreshUrl: () => Promise<string | null>;
};

/** Placeholder row returned while a chunk is loading. */
const LOADING_ROW: Record<string, unknown> = { __chunkedLoading: true };

/**
 * Hook that provides rowCount and getRow for chunked parquet loading.
 * Loads chunks on demand (when getRow is called for an index in an unloaded chunk).
 * Columns are taken from the first loaded chunk (chunk 0 is loaded eagerly so we have columns).
 */
export function useChunkedParquetRowProvider({
  downloadUrl,
  totalRowCount,
  chunkSize = CHUNKED_PARQUET_CHUNK_SIZE,
  getRefreshUrl,
}: ChunkedParquetProviderParams): {
  rowCount: number;
  getRow: (rowIndex: number) => Record<string, unknown>;
  columns: arrow.Field<any>[] | null;
  isColumnsReady: boolean;
} {
  const [chunkCache, setChunkCache] = useState<Map<number, ParquetChunkResult>>(
    () => new Map()
  );
  const [columns, setColumns] = useState<arrow.Field<any>[] | null>(null);
  const loadingRef = useRef<Set<number>>(new Set());
  const urlRef = useRef(downloadUrl);
  const parquetFileRef = useRef<Awaited<
    ReturnType<typeof openParquetFileFromUrl>
  > | null>(null);
  urlRef.current = downloadUrl;

  // Log max rows when chunked provider is used
  useEffect(() => {
    if (totalRowCount > 0) {
      console.log(
        "[chunked parquet] max number of rows:",
        totalRowCount.toLocaleString()
      );
    }
  }, [totalRowCount]);

  // Free parquet file when URL changes or unmount
  useEffect(() => {
    return () => {
      parquetFileRef.current?.free();
      parquetFileRef.current = null;
    };
  }, [downloadUrl]);

  const loadChunk = useCallback(
    async (chunkIndex: number) => {
      if (loadingRef.current.has(chunkIndex)) return;
      loadingRef.current.add(chunkIndex);
      const offset = chunkIndex * chunkSize;
      const limit = Math.min(chunkSize, totalRowCount - offset);
      if (limit <= 0) {
        loadingRef.current.delete(chunkIndex);
        return;
      }
      try {
        console.log(
          "[chunked parquet] loading new chunk:",
          chunkIndex,
          `(rows ${offset}-${offset + limit - 1})`
        );
        if (!parquetFileRef.current) {
          parquetFileRef.current = await openParquetFileFromUrl(
            urlRef.current,
            getRefreshUrl
          );
        }
        const result = await readChunkFromParquetFile(
          parquetFileRef.current,
          offset,
          limit
        );
        setChunkCache((prev) => {
          const next = new Map(prev);
          next.set(chunkIndex, result);
          return next;
        });
        if (result.columns.length > 0) {
          setColumns((prev) => prev ?? result.columns);
        }
        console.log("[chunked parquet] chunk loaded:", chunkIndex);

        // [matrix transform debug] Log schema and sample rows when first chunk loads
        if (chunkIndex === 0 && result.arrowTable.numRows > 0) {
          const columnNames = result.columns.map((c) => c.name);
          console.log("[matrix transform debug] Parquet column names:", columnNames);
          const row0 = getRowFromArrowTable(
            result.arrowTable,
            result.columns,
            0
          ) as Record<string, unknown>;
          console.log(
            "[matrix transform debug] Row 0 keys:",
            Object.keys(row0),
            "| segmentation:",
            row0.segmentation,
            "| measure:",
            row0.measure,
            "| measure_rank:",
            row0.measure_rank,
            "| segmentation_rank:",
            row0.segmentation_rank
          );
          if (result.arrowTable.numRows > 1) {
            const row1 = getRowFromArrowTable(
              result.arrowTable,
              result.columns,
              1
            ) as Record<string, unknown>;
            console.log(
              "[matrix transform debug] Row 1 measure:",
              row1.measure,
              "| measure_rank:",
              row1.measure_rank
            );
          }
        }
      } catch (e) {
        console.error("chunked_parquet_provider: loadChunk failed", e);
      } finally {
        loadingRef.current.delete(chunkIndex);
      }
    },
    [chunkSize, totalRowCount, getRefreshUrl]
  );

  // Eagerly load chunk 0 so we have columns for the table header
  const hasChunkZero = chunkCache.has(0);
  useEffect(() => {
    if (totalRowCount > 0 && downloadUrl && !hasChunkZero) {
      loadChunk(0);
    }
  }, [downloadUrl, totalRowCount, loadChunk, chunkCache, hasChunkZero]);

  const getRow = useCallback(
    (rowIndex: number): Record<string, unknown> => {
      if (rowIndex < 0 || rowIndex >= totalRowCount) {
        return {};
      }
      const chunkIndex = Math.floor(rowIndex / chunkSize);
      const rowInChunk = rowIndex % chunkSize;
      const chunk = chunkCache.get(chunkIndex);
      if (chunk) {
        const row = getRowFromArrowTable(
          chunk.arrowTable,
          chunk.columns,
          rowInChunk
        );
        return row as Record<string, unknown>;
      }
      loadChunk(chunkIndex);
      return LOADING_ROW;
    },
    [totalRowCount, chunkSize, chunkCache, loadChunk]
  );

  return {
    rowCount: totalRowCount,
    getRow,
    columns,
    isColumnsReady: columns !== null,
  };
}
